import { create } from 'zustand'
import { useFillQueueStore } from './useFillQueueStore'
import { useFillAttemptStore, attemptKey } from './useFillAttemptStore'
import { useIncompleteDataStore } from './useIncompleteDataStore'

// Fronta nabídek dotažení pro režim 'ask' (viz doc/plans/to-fill-data-fetching.md, krok 7).
// Sbírá nabídnuté joby pod jejich `key`. Když je `pending` neprázdné a režim je
// 'ask', FillPromptDialog zobrazí JEDEN modal se souhrnem.
//
// job = { toFill: [...], onFilled, onSettled, retryCtx }

export const useFillPromptStore = create((set, get) => ({
  pending: {}, // { [key]: job }

  // Další nabídka pro stejný key se PŘIDÁ k čekající (nové položky na konec, bez
  // duplicit) — nepřepisovat, jinak by dříve nabídnuté řady z dialogu vypadly.
  offer: ({ key, job }) => set((s) => {
    const cur = s.pending[key]
    if (!cur) return { pending: { ...s.pending, [key]: job } }
    const sig = (it) => `${it.endpoint}|${JSON.stringify(it.params)}`
    const have = new Set((cur.toFill ?? []).map(sig))
    const add = (job.toFill ?? []).filter((it) => !have.has(sig(it)))
    if (!add.length) return s
    return { pending: { ...s.pending, [key]: { ...job, toFill: [...cur.toFill, ...add] } } }
  }),

  // Je položka už v nabídnutém jobu pro tenhle key? (dedup pro useToFill)
  has: (key, item) => {
    const job = get().pending[key]
    if (!job) return false
    const target = `${item.endpoint}|${JSON.stringify(item.params)}`
    return (job.toFill ?? []).some(
      (it) => `${it.endpoint}|${JSON.stringify(it.params)}` === target
    )
  },

  withdraw: (key) =>
    set((s) => {
      if (!(key in s.pending)) return s
      const next = { ...s.pending }
      delete next[key]
      return { pending: next }
    }),

  // „Dotáhnout" — uživatel chce teď, i když je okno v cache pokusů blokované.
  // invalidate(klíč) smaže záznam → runToFill projde a onSettled ho zapíše znovu.
  accept: () => {
    const { pending } = get()
    const enqueue = useFillQueueStore.getState().enqueue
    const invalidate = useFillAttemptStore.getState().invalidate
    for (const [key, job] of Object.entries(pending)) {
      ;(job.toFill ?? []).forEach((it) => invalidate(attemptKey(it)))
      enqueue({ key, jobs: [job] })
    }
    set({ pending: {} })
  },

  // „Nedotahovat" — uživatel řekl ne → record() (pauza T min) + rovnou zapiš do
  // registru neúplných řad (badge se objeví hned, useToFill to při příštím renderu
  // jen potvrdí — po record() je položka „v pauze" → stejný seznam).
  dismiss: () => {
    const { pending } = get()
    const record = useFillAttemptStore.getState().record
    const setIncomplete = useIncompleteDataStore.getState().set
    for (const [screenKey, job] of Object.entries(pending)) {
      const items = job.toFill ?? []
      items.forEach((it) => record(attemptKey(it), job.retryCtx))
      setIncomplete(
        screenKey,
        items.map((it) => ({ key: attemptKey(it), item: it, retryCtx: job.retryCtx })),
      )
    }
    set({ pending: {} })
  },

  // Součet položek přes všechny nabídky.
  count: () =>
    Object.values(get().pending).reduce((n, job) => n + (job.toFill?.length ?? 0), 0),
}))
